// import React, { useState, useEffect } from 'react';
import { useState, useEffect } from 'react';
import { ListedUser } from '../../types'; // Assuming types are one level up
import { useNotification } from '../../contexts/NotificationContext'; // Import notification hook
import { TrashIcon, ShieldCheckIcon, ShieldExclamationIcon, SparklesIcon } from '@heroicons/react/24/outline'; // Import icons

// Define structure for the API response
interface AdminUsersResponse {
  users: ListedUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export default function AdminUsers() {
  const [usersData, setUsersData] = useState<AdminUsersResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const { addNotification } = useNotification(); // Use notification hook
  // Action loading states
  const [updatingAdminStatus, setUpdatingAdminStatus] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  const [updatingAIPermission, setUpdatingAIPermission] = useState<string | null>(null);

  useEffect(() => {
    fetchAdminUsers(page);
  }, [page]);

  const fetchAdminUsers = async (currentPage: number) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/admin/users?page=${currentPage}&limit=25`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) {
        throw new Error((data as {error: string})?.error || 'Failed to fetch users');
      }
      setUsersData(data as AdminUsersResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load users');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleToggleAdmin = async (userId: string, currentIsAdmin: boolean) => {
    setUpdatingAdminStatus(userId);
    setError('');
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_admin: !currentIsAdmin })
      });
      const data = await response.json();
      if (!response.ok) throw new Error((data as {error: string})?.error || 'Failed to update admin status');
      addNotification('Admin status updated!', 'success');
      fetchAdminUsers(page); // Refresh user list
    } catch (err) {
       const errorMsg = err instanceof Error ? err.message : 'Could not update status';
       setError(errorMsg);
       addNotification(errorMsg, 'error');
    } finally {
       setUpdatingAdminStatus(null);
    }
  };

  const handleToggleAIAssist = async (userId: string, currentAIPermission: boolean) => {
    // 1. Optimistically update local state
    const originalUsersData = usersData; // Store original state for potential revert
    setUsersData(prevData => {
      if (!prevData) return null;
      return {
        ...prevData,
        users: prevData.users.map(u => 
          u.id === userId ? { ...u, can_use_ai_assistant: !currentAIPermission } : u
        )
      };
    });

    setUpdatingAIPermission(userId);
    setError('');
    try {
      // 2. Make API call
      const response = await fetch(`/api/admin/users/${userId}/permissions`, { 
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ can_use_ai_assistant: !currentAIPermission })
      });
      const data = await response.json();
      if (!response.ok) throw new Error((data as {error: string})?.error || 'Failed to update AI permission');
      addNotification('AI permission updated!', 'success');
      // No need to call fetchAdminUsers immediately if API succeeds, state is already updated
      // fetchAdminUsers(page); // Can remove or keep for eventual consistency check
    } catch (err) {
       // 3. Revert state on error
       setUsersData(originalUsersData); // Revert optimistic update
       const errorMsg = err instanceof Error ? err.message : 'Could not update AI permission';
       setError(errorMsg);
       addNotification(errorMsg, 'error');
    } finally {
       setUpdatingAIPermission(null);
    }
  };

  const handleDeleteUser = async (userId: string, username: string) => {
    if (!window.confirm(`Are you sure you want to DELETE the user "${username}"? This is irreversible!`)) return;
    setDeletingUser(userId);
    setError('');
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
       const data = await response.json();
      if (!response.ok) throw new Error((data as {error: string})?.error || 'Failed to delete user');
      addNotification('User deleted successfully!', 'success');
      fetchAdminUsers(1); // Refresh list and go back to page 1
    } catch (err) {
       const errorMsg = err instanceof Error ? err.message : 'Could not delete user';
       setError(errorMsg);
       addNotification(errorMsg, 'error');
    } finally {
       setDeletingUser(null);
    }
  };

  // Basic Pagination Component (can be extracted)
  const Pagination = () => {
      if (!usersData || usersData.pagination.pages <= 1) return null;
      const { page, pages } = usersData.pagination;
      return (
        <div className="flex justify-center items-center space-x-2 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-secondary text-sm">Prev</button>
            <span>Page {page} of {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="btn-secondary text-sm">Next</button>
        </div>
      );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin: All Users</h1>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="shadow-md dark:shadow-none rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Admin?</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Joined</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th> 
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr><td colSpan={6} className="text-center p-4 dark:text-gray-400">Loading...</td></tr>
              ) : !usersData || usersData.users.length === 0 ? (
                <tr><td colSpan={6} className="text-center p-4 dark:text-gray-400">No users found.</td></tr>
              ) : (
                usersData.users.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{user.username}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.email || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.is_guest ? 'Guest' : 'Registered'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center"><span className={`inline-block h-3 w-3 rounded-full ${user.is_admin ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}></span></td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{new Date(user.created_at).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2"> 
                       <button
                         onClick={() => handleToggleAdmin(user.id, user.is_admin)}
                         disabled={updatingAdminStatus === user.id || deletingUser === user.id || updatingAIPermission === user.id}
                         className={`p-1 rounded ${user.is_admin ? 'text-yellow-600 hover:bg-yellow-100 dark:text-yellow-400 dark:hover:bg-yellow-900/50' : 'text-green-600 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/50'} disabled:opacity-50`}
                         title={user.is_admin ? 'Revoke Admin' : 'Make Admin'}
                       >
                         {updatingAdminStatus === user.id ? (
                           <svg className="animate-spin h-4 w-4 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                           </svg>
                         ) : user.is_admin ? (
                            <ShieldExclamationIcon className="h-4 w-4" />
                         ) : (
                            <ShieldCheckIcon className="h-4 w-4" />
                         )}
                       </button>
                       <button
                         onClick={() => handleToggleAIAssist(user.id, user.can_use_ai_assistant)}
                         disabled={updatingAdminStatus === user.id || deletingUser === user.id || updatingAIPermission === user.id}
                         className={`p-1 rounded ${user.can_use_ai_assistant ? 'text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/50' : 'text-gray-400 hover:bg-gray-100 dark:text-gray-500 dark:hover:bg-gray-700'} disabled:opacity-50`}
                         title={user.can_use_ai_assistant ? 'Disable AI Assist' : 'Enable AI Assist'}
                       >
                         {updatingAIPermission === user.id ? (
                            <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                         ) : (
                            <SparklesIcon className={`h-4 w-4 ${user.can_use_ai_assistant ? '' : 'opacity-50'}`} />
                         )}
                       </button>
                       <button
                         onClick={() => handleDeleteUser(user.id, user.username)}
                         disabled={updatingAdminStatus === user.id || deletingUser === user.id || updatingAIPermission === user.id}
                         className="p-1 rounded text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/50 disabled:opacity-50"
                         title="Delete User"
                       >
                          {deletingUser === user.id ? (
                              <svg className="animate-spin h-4 w-4 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                               </svg>
                          ) : (
                              <TrashIcon className="h-4 w-4" />
                          )}
                       </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination />
    </div>
  );
} 